import { useHydratedExperimentVariant } from 'src/utils/hydrated-experiment'
import { Experiments } from '../constants'

type Variant = 'control' | 'treatment'

const EXPERIMENT_KEY = Experiments.SeoHeaderH1H2

/**
 * SSR-safe variant lookup — same rationale as
 * `useHomeInputboxRefreshExperiment`.
 *
 * This experiment gates the `<h1>The World's #1 AI Anime Generator</h1> +
 * <h2>Create with 1M+ community models …</h2>` SEO header block at the
 * top of `ArtworkListPage` (above the prompt input + grid). When the
 * lookup goes through `useExperimentVariant`, SSR + the first hydrate
 * commit see `'control'` (the Amplitude SDK's `__HYDRATED_EXPERIMENTS__`
 * isn't consulted, the warm cache hits only after `useEffect`), so the
 * SSR HTML omits the header entirely and the artwork grid mounts at
 * the top of the page. A few ticks later the experiment resolves to
 * `'treatment'`, the SEO header block flips on, and the entire grid
 * gets pushed down by ~8rem (h1 + h2 + spacing). That visible "the
 * grid I was looking at on first paint just shifted down a row" is
 * what the user reported as the SSR-vs-hydrate mismatch — the
 * artwork *contents* never change, only their on-screen position,
 * because a CLS-inducing sibling appeared above them.
 *
 * Reading the variant from `__HYDRATED_EXPERIMENTS__` (populated by
 * `experimentMiddleware` → root loader → `<HydratedExperimentProvider>`)
 * makes the SSR HTML and the first hydrate render agree on whether
 * to ship the header, so the grid lands at its final position on the
 * very first paint and stays there.
 */
export function useSeoHeaderH1H2Experiment() {
  const { value: variant } = useHydratedExperimentVariant<Variant>(
    EXPERIMENT_KEY,
    'control',
  )

  return {
    variant,
    isTreatment: variant === 'treatment',
  }
}
